// apps/web/src/pages/ProductForm.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, X, FileText, Image as ImageIcon } from 'lucide-react';
import pb from '../../lib/pocketbaseClient';

// --- Schema � matches PocketBase 'products' collection ---
const productSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  category: z.string().optional(),
  vastu_direction: z.string().optional(),
  building_type: z.string().optional(),
  bedroom_type: z.string().optional(),
  area: z.coerce.number().min(0).optional(),
  bathrooms: z.coerce.number().min(0).optional(),
  popular: z.boolean().optional(),
  status: z.enum(['active', 'inactive']).default('active'),
  youtubeVideoUrl: z.string().url().or(z.literal('')).optional(),
  floorPlanMrp: z.coerce.number().min(0).optional(),
  floorPlanPrice: z.coerce.number().min(0).optional(),
  fullDesignMrp: z.coerce.number().min(0).optional(),
  fullDesignPrice: z.coerce.number().min(0).optional(),
  fullDesignIncludes: z.string().optional(),
});

// --- Universal file URL helper (works with any SDK version) ---
const getFileUrlUniversal = (record, filename) => {
  if (!filename || !record) return null;
  try {
    if (pb.files && typeof pb.files.getURL === 'function') {
      return pb.files.getURL(record, filename);
    }
    if (pb.files && typeof pb.files.getUrl === 'function') {
      return pb.files.getUrl(record, filename);
    }
    if (typeof pb.getFileUrl === 'function') {
      return pb.getFileUrl(record, filename);
    }
    // Manual fallback
    const base = pb.baseUrl || '';
    const collection = record.collectionId || record.collectionName || 'products';
    return `${base}/api/files/${collection}/${record.id}/${filename}`;
  } catch {
    return null;
  }
};

const ProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const [loading, setLoading] = useState(isEditing);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('common');

  // --- Product record (for file URL generation) ---
  const [productRecord, setProductRecord] = useState(null);

  // --- File states ---
  const [featuredImageFile, setFeaturedImageFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [floorPlanFiles, setFloorPlanFiles] = useState([]);
  const [fullDesignFiles, setFullDesignFiles] = useState([]);

  // --- Existing filenames from record ---
  const [existingFiles, setExistingFiles] = useState({
    featuredImage: null,
    pdfFile: null,
    galleryImages: [],
    floorPlanFiles: [],
    fullDesignFiles: [],
  });

  // --- Removed filenames (to delete) ---
  const [removedFiles, setRemovedFiles] = useState({
    galleryImages: [],
    floorPlanFiles: [],
    fullDesignFiles: [],
  });

  // --- Clear single-file flags ---
  const [clearFeatured, setClearFeatured] = useState(false);
  const [clearPdf, setClearPdf] = useState(false);

  // --- React Hook Form ---
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: zodResolver(productSchema),
    defaultValues: {
      name: '',
      description: '',
      category: '',
      vastu_direction: '',
      building_type: '',
      bedroom_type: '',
      area: undefined,
      bathrooms: undefined,
      popular: false,
      status: 'active',
      youtubeVideoUrl: '',
      floorPlanMrp: undefined,
      floorPlanPrice: undefined,
      fullDesignMrp: undefined,
      fullDesignPrice: undefined,
      fullDesignIncludes: '',
    },
  });

  // --- Fetch product for editing ---
  useEffect(() => {
    if (isEditing) {
      const fetchProduct = async () => {
        try {
          const record = await pb.collection('products').getOne(id);
          setProductRecord(record);
          reset({
            name: record.name || '',
            description: record.description || '',
            category: record.category || '',
            vastu_direction: record.vastu_direction || '',
            building_type: record.building_type || '',
            bedroom_type: record.bedroom_type || '',
            area: record.area || undefined,
            bathrooms: record.bathrooms || undefined,
            popular: record.popular || false,
            status: record.status || 'active',
            youtubeVideoUrl: record.youtubeVideoUrl || '',
            floorPlanMrp: record.floorPlanMrp || undefined,
            floorPlanPrice: record.floorPlanPrice || undefined,
            fullDesignMrp: record.fullDesignMrp || undefined,
            fullDesignPrice: record.fullDesignPrice || undefined,
            fullDesignIncludes: record.fullDesignIncludes || '',
          });
          setExistingFiles({
            featuredImage: record.featuredImage || null,
            pdfFile: record.pdfFile || null,
            galleryImages: record.galleryImages || [],
            floorPlanFiles: record.floorPlanFiles || [],
            fullDesignFiles: record.fullDesignFiles || [],
          });
        } catch (error) {
          toast.error('Failed to load product');
          navigate('/products');
        } finally {
          setLoading(false);
        }
      };
      fetchProduct();
    } else {
      setLoading(false);
    }
  }, [id, isEditing, reset, navigate]);

  // --- File handlers ---
  const handleSingleFile = (setter, setClear, e) => {
    const file = e.target.files?.[0] || null;
    setter(file);
    if (setClear) setClear(false);
    e.target.value = '';
  };

  const handleMultiFile = (setter, e) => {
    const files = Array.from(e.target.files || []);
    setter((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const removeNewFile = (setter, index) => {
    setter((prev) => prev.filter((_, i) => i !== index));
  };

  const removeExistingFile = (field, filename) => {
    setExistingFiles((prev) => ({
      ...prev,
      [field]: prev[field].filter((f) => f !== filename),
    }));
    setRemovedFiles((prev) => ({
      ...prev,
      [field]: [...prev[field], filename],
    }));
  };

  const clearExistingSingle = (field, setClear) => {
    setExistingFiles((prev) => ({ ...prev, [field]: null }));
    setClear(true);
  };

  // --- Helper to get file URL using the universal method ---
  const getFileUrl = (filename) => {
    if (!filename) return null;
    if (!productRecord) return null;
    return getFileUrlUniversal(productRecord, filename);
  };

  // --- Submit ---
  const generateSlug = (text) =>
    text
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]+/g, '')
      .replace(/\-\-+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '');

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      const payload = { ...data };

      if (!isEditing) {
        let baseSlug = generateSlug(data.name);
        let slug = baseSlug;
        let counter = 1;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          try {
            await pb.collection('products').getFirstListItem(`slug="${slug}"`);
            slug = `${baseSlug}-${counter++}`;
          } catch {
            break;
          }
        }
        payload.slug = slug;
      }

      const formData = new FormData();

      // Append simple fields
      for (const [key, value] of Object.entries(payload)) {
        if (value !== undefined && value !== null && value !== '') {
          if (key === 'popular') {
            formData.append(key, value ? 'true' : 'false');
          } else if (key !== 'featuredImage' && key !== 'pdfFile') {
            formData.append(key, String(value));
          }
        }
      }

      // Single-file fields
      if (featuredImageFile) formData.append('featuredImage', featuredImageFile);
      else if (clearFeatured) formData.append('featuredImage', '');

      if (pdfFile) formData.append('pdfFile', pdfFile);
      else if (clearPdf) formData.append('pdfFile', '');

      // Multi-file fields: new files
      galleryFiles.forEach((f) => formData.append('galleryImages', f));
      floorPlanFiles.forEach((f) => formData.append('floorPlanFiles', f));
      fullDesignFiles.forEach((f) => formData.append('fullDesignFiles', f));

      // Multi-file removals
      removedFiles.galleryImages.forEach((f) => formData.append('galleryImages-', f));
      removedFiles.floorPlanFiles.forEach((f) => formData.append('floorPlanFiles-', f));
      removedFiles.fullDesignFiles.forEach((f) => formData.append('fullDesignFiles-', f));

      if (isEditing) {
        await pb.collection('products').update(id, formData);
        toast.success('Product updated');
      } else {
        await pb.collection('products').create(formData);
        toast.success('Product created');
      }
      navigate('/products');
    } catch (error) {
      toast.error(error?.message || 'Failed to save');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/products')} className="p-2 hover:bg-slate-100 rounded-full">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-2xl font-bold">{isEditing ? 'Edit Product' : 'Add Product'}</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-xl shadow p-6 space-y-6">
        {/* Tabs */}
        <div className="grid grid-cols-3 gap-2 p-1 bg-slate-100 rounded-xl">
          {['common', 'floor_plan', 'full_design'].map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-4 rounded-lg font-medium transition ${
                activeTab === tab ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-200'
              }`}
            >
              {tab === 'common' ? 'Common Details' : tab === 'floor_plan' ? 'Floor Plan' : 'Full Design'}
            </button>
          ))}
        </div>

        {/* ========== COMMON TAB ========== */}
        {activeTab === 'common' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700">Name *</label>
              <input {...register('name')} className="w-full mt-1 px-3 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500" />
              {errors.name && <p className="text-red-500 text-sm">{errors.name.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Description</label>
              <textarea {...register('description')} rows="3" className="w-full mt-1 px-3 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500" />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Category</label>
              <select {...register('category')} className="w-full mt-1 px-3 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500">
                <option value="">Select Category</option>
                <option value="Residential">Residential</option>
                <option value="Commercial">Commercial</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Vastu Direction</label>
              <select {...register('vastu_direction')} className="w-full mt-1 px-3 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500">
                <option value="">Select Direction</option>
                <option value="East Facing">East Facing</option>
                <option value="North Facing">North Facing</option>
                <option value="West Facing">West Facing</option>
                <option value="South Facing">South Facing</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Building Type</label>
              <select {...register('building_type')} className="w-full mt-1 px-3 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500">
                <option value="">Select Building Type</option>
                <option value="Single Stored">Single Stored</option>
                <option value="Two Storied (G+1)">Two Storied (G+1)</option>
                <option value="Three Storied (G+2)">Three Storied (G+2)</option>
                <option value="Four Storied (G+3)">Four Storied (G+3)</option>
                <option value="Five Storied (G+4)">Five Storied (G+4)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Bedroom Type</label>
              <select {...register('bedroom_type')} className="w-full mt-1 px-3 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500">
                <option value="">Select Bedroom Type</option>
                <option value="1 BHK">1 BHK</option>
                <option value="2 BHK">2 BHK</option>
                <option value="3 BHK">3 BHK</option>
                <option value="4 BHK">4 BHK</option>
                <option value="5+ BHK">5+ BHK</option>
                <option value="Duplex">Duplex</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Area (sq ft)</label>
              <input type="number" {...register('area')} className="w-full mt-1 px-3 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500" placeholder="e.g., 1200" />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Bathrooms</label>
              <input type="number" {...register('bathrooms')} className="w-full mt-1 px-3 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500" placeholder="e.g., 2" />
            </div>

            <div className="flex items-center gap-2">
              <input type="checkbox" {...register('popular')} className="w-4 h-4" />
              <label className="text-sm font-medium text-slate-700">Mark as Popular</label>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Status</label>
              <select {...register('status')} className="w-full mt-1 px-3 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">YouTube URL</label>
              <input {...register('youtubeVideoUrl')} placeholder="https://..." className="w-full mt-1 px-3 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500" />
              {errors.youtubeVideoUrl && <p className="text-red-500 text-sm">{errors.youtubeVideoUrl.message}</p>}
            </div>

            {/* --- Featured Image --- */}
            <div>
              <label className="block text-sm font-medium text-slate-700">Featured Image</label>
              <div className="border-2 border-dashed rounded-xl p-4 mt-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleSingleFile(setFeaturedImageFile, setClearFeatured, e)}
                  className="hidden"
                  id="featured"
                />
                <label htmlFor="featured" className="cursor-pointer inline-block bg-indigo-50 px-4 py-2 rounded-lg hover:bg-indigo-100 transition">
                  Upload
                </label>

                {featuredImageFile ? (
                  <div className="mt-2 flex items-center gap-3">
                    <img
                      src={URL.createObjectURL(featuredImageFile)}
                      alt="Preview"
                      className="w-16 h-16 object-cover rounded border border-slate-200"
                    />
                    <span className="text-sm font-medium">{featuredImageFile.name}</span>
                    <button
                      type="button"
                      onClick={() => { setFeaturedImageFile(null); setClearFeatured(false); }}
                      className="p-1 rounded-full hover:bg-red-50 text-red-500 transition"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : existingFiles.featuredImage ? (
                  <div className="mt-2 flex items-center gap-3">
                    <img
                      src={getFileUrl(existingFiles.featuredImage)}
                      alt="Current featured"
                      className="w-16 h-16 object-cover rounded border border-slate-200"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        const parent = e.target.parentNode;
                        const fallback = document.createElement('div');
                        fallback.className = 'w-16 h-16 bg-slate-100 rounded border border-slate-200 flex items-center justify-center text-slate-400';
                        fallback.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>';
                        parent.appendChild(fallback);
                        e.target.remove();
                      }}
                    />
                    <span className="text-sm">Current: {existingFiles.featuredImage}</span>
                    <button
                      type="button"
                      onClick={() => clearExistingSingle('featuredImage', setClearFeatured)}
                      className="p-1 rounded-full hover:bg-red-50 text-red-500 transition"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <span className="ml-2 text-sm text-slate-400">No image uploaded</span>
                )}
              </div>
            </div>

            {/* --- PDF Document --- */}
            <div>
              <label className="block text-sm font-medium text-slate-700">PDF Document</label>
              <div className="border-2 border-dashed rounded-xl p-4 mt-1">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => handleSingleFile(setPdfFile, setClearPdf, e)}
                  className="hidden"
                  id="pdf"
                />
                <label htmlFor="pdf" className="cursor-pointer inline-block bg-indigo-50 px-4 py-2 rounded-lg hover:bg-indigo-100 transition">
                  Upload
                </label>
                {pdfFile ? (
                  <span className="ml-2 text-sm">
                    New: {pdfFile.name}{' '}
                    <button type="button" onClick={() => { setPdfFile(null); setClearPdf(false); }} className="text-red-500 ml-1">
                      <X className="w-4 h-4 inline" />
                    </button>
                  </span>
                ) : existingFiles.pdfFile ? (
                  <span className="ml-2 text-sm">
                    Current: {existingFiles.pdfFile}{' '}
                    <button type="button" onClick={() => clearExistingSingle('pdfFile', setClearPdf)} className="text-red-500 ml-1">
                      <X className="w-4 h-4 inline" />
                    </button>
                  </span>
                ) : (
                  <span className="ml-2 text-sm text-slate-400">No PDF</span>
                )}
              </div>
            </div>

            {/* --- Gallery Images --- */}
            <div>
              <label className="block text-sm font-medium text-slate-700">Gallery Images</label>
              <div className="border-2 border-dashed rounded-xl p-4 mt-1">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => handleMultiFile(setGalleryFiles, e)}
                  className="hidden"
                  id="gallery"
                />
                <label htmlFor="gallery" className="cursor-pointer inline-block bg-indigo-50 px-4 py-2 rounded-lg hover:bg-indigo-100 transition">
                  Add Images
                </label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {existingFiles.galleryImages.map((img) => {
                    const url = getFileUrl(img);
                    return (
                      <div key={`existing-${img}`} className="relative group">
                        {url ? (
                          <img
                            src={url}
                            alt=""
                            className="w-16 h-16 object-cover rounded border border-slate-200"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-16 h-16 bg-slate-100 rounded border border-slate-200 flex items-center justify-center text-slate-400">
                            <ImageIcon className="w-6 h-6" />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => removeExistingFile('galleryImages', img)}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                  {galleryFiles.map((f, i) => (
                    <div key={`new-${i}`} className="relative group">
                      <img
                        src={URL.createObjectURL(f)}
                        alt=""
                        className="w-16 h-16 object-cover rounded border border-slate-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeNewFile(setGalleryFiles, i)}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== FLOOR PLAN TAB ========== */}
        {activeTab === 'floor_plan' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700">MRP (?)</label>
              <input type="number" {...register('floorPlanMrp')} className="w-full mt-1 px-3 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Selling Price (?)</label>
              <input type="number" {...register('floorPlanPrice')} className="w-full mt-1 px-3 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Files</label>
              <div className="border-2 border-dashed rounded-xl p-4 mt-1">
                <input
                  type="file"
                  multiple
                  accept=".pdf,.dwg,.zip"
                  onChange={(e) => handleMultiFile(setFloorPlanFiles, e)}
                  className="hidden"
                  id="fp-files"
                />
                <label htmlFor="fp-files" className="cursor-pointer inline-block bg-indigo-50 px-4 py-2 rounded-lg hover:bg-indigo-100 transition">
                  Upload Files
                </label>
                {existingFiles.floorPlanFiles.map((f) => (
                  <div key={f} className="flex items-center gap-2 text-sm mt-1">
                    <FileText className="w-4 h-4" /> {f}
                    <button type="button" onClick={() => removeExistingFile('floorPlanFiles', f)} className="text-red-500">
                      <X className="w-4 h-4 inline" />
                    </button>
                  </div>
                ))}
                {floorPlanFiles.map((f, i) => (
                  <div key={`new-${i}`} className="flex items-center gap-2 text-sm text-indigo-600 mt-1">
                    <FileText className="w-4 h-4" /> {f.name}
                    <button type="button" onClick={() => removeNewFile(setFloorPlanFiles, i)} className="text-red-500">
                      <X className="w-4 h-4 inline" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ========== FULL DESIGN TAB ========== */}
        {activeTab === 'full_design' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700">MRP (?)</label>
              <input type="number" {...register('fullDesignMrp')} className="w-full mt-1 px-3 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Selling Price (?)</label>
              <input type="number" {...register('fullDesignPrice')} className="w-full mt-1 px-3 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">What's Included</label>
              <textarea {...register('fullDesignIncludes')} rows="3" className="w-full mt-1 px-3 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Files</label>
              <div className="border-2 border-dashed rounded-xl p-4 mt-1">
                <input
                  type="file"
                  multiple
                  accept=".pdf,.dwg,.zip"
                  onChange={(e) => handleMultiFile(setFullDesignFiles, e)}
                  className="hidden"
                  id="fd-files"
                />
                <label htmlFor="fd-files" className="cursor-pointer inline-block bg-indigo-50 px-4 py-2 rounded-lg hover:bg-indigo-100 transition">
                  Upload Files
                </label>
                {existingFiles.fullDesignFiles.map((f) => (
                  <div key={f} className="flex items-center gap-2 text-sm mt-1">
                    <FileText className="w-4 h-4" /> {f}
                    <button type="button" onClick={() => removeExistingFile('fullDesignFiles', f)} className="text-red-500">
                      <X className="w-4 h-4 inline" />
                    </button>
                  </div>
                ))}
                {fullDesignFiles.map((f, i) => (
                  <div key={`new-${i}`} className="flex items-center gap-2 text-sm text-indigo-600 mt-1">
                    <FileText className="w-4 h-4" /> {f.name}
                    <button type="button" onClick={() => removeNewFile(setFullDesignFiles, i)} className="text-red-500">
                      <X className="w-4 h-4 inline" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Submit */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button type="button" onClick={() => navigate('/products')} className="px-4 py-2 border rounded-lg hover:bg-slate-50">
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-70 flex items-center gap-2"
          >
            {submitting && <Loader2 className="animate-spin w-4 h-4" />}
            {isEditing ? 'Update Product' : 'Save Product'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;